// /admin/system — system health dashboard (T126).
//
// Three panels:
//   1. Service health — coloured dot + label per downstream dep
//      (postgres, opensearch, s3, nats).
//   2. Queue depth — numeric card showing pending NATS scan jobs.
//   3. Scan-rate sparkline — 12 × 5-minute bars for the last hour,
//      rendered as an SVG bar chart with no external library dependency.
//
// Backend: GET /api/v1/admin/system (org_admin role required).
// Auto-refreshes every 15 seconds via SWR refreshInterval.

import useSWR from 'swr';
import {
  Activity,
  CheckCircle2,
  Database,
  Loader2,
  MinusCircle,
  RefreshCw,
  Search,
  Server,
  ShieldAlert,
  XCircle,
} from 'lucide-react';
import { ApiError, AdvisorySyncStatus, SystemStatus, ADMIN_SYSTEM_KEY, fetcher } from '../lib/api';
import { PageHeader } from '../components/ui';

// Alert threshold mirrored from charts/orbital-lint/values.yaml
// observability.prometheusRules.advisorySyncAgeThresholdSeconds (7 days).
const ADVISORY_SYNC_STALE_THRESHOLD_SECONDS = 7 * 24 * 60 * 60;

// Map dep names to friendly labels + icons.
const DEP_META: Record<string, { label: string; Icon: typeof Server }> = {
  postgres:    { label: 'PostgreSQL',  Icon: Database },
  opensearch:  { label: 'OpenSearch',  Icon: Search   },
  s3:          { label: 'Object Store (S3)', Icon: Server },
  nats:        { label: 'NATS JetStream',    Icon: Activity },
};

// Fallback ordering when checks is empty / has unknown keys.
const DEP_ORDER = ['postgres', 'opensearch', 's3', 'nats'];

// ---------------------------------------------------------------------------
// Page root
// ---------------------------------------------------------------------------

export function AdminSystem() {
  const { data, error, isLoading, isValidating, mutate } =
    useSWR<SystemStatus>(ADMIN_SYSTEM_KEY, fetcher, {
      refreshInterval: 15_000,
      revalidateOnFocus: true,
    });

  return (
    <div className="max-w-3xl mx-auto p-8 w-full">
      <PageHeader
        eyebrow="orbital · admin"
        icon={<Server />}
        title="System"
        description="Live health of downstream services, queue depth, and recent scan activity."
        actions={
          <button
            type="button"
            onClick={() => mutate()}
            disabled={isValidating}
            aria-label="Refresh system status"
            className="
              inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md
              text-xs font-semibold uppercase tracking-[0.08em]
              border border-border text-fg-muted hover:text-fg hover:border-fg/20
              transition-colors disabled:opacity-40 disabled:cursor-not-allowed
            "
          >
            <RefreshCw
              className={`h-3.5 w-3.5 ${isValidating ? 'animate-spin' : ''}`}
              aria-hidden="true"
            />
            Refresh
          </button>
        }
      />

      {(() => {
        if (isLoading) return <LoadingState />;
        if (error) return <ErrorBanner error={error} />;
        if (data) return (
          <>
            <ServiceHealthCard checks={data.checks} disabled={data.disabled} />
            <QueueDepthCard depth={data.queue_depth} />
            <AdvisorySyncCard status={data.advisory_sync} />
            <SparklineCard buckets={data.scan_rate_sparkline} />
          </>
        );
        return null;
      })()}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ServiceHealthCard
// ---------------------------------------------------------------------------

function ServiceHealthCard({
  checks,
  disabled,
}: {
  readonly checks?: Record<string, string>;
  readonly disabled?: Record<string, string>;
}) {
  // Merge known deps with whatever the server returned, preserving display order.
  const keys = checks || disabled
    ? [...new Set([...DEP_ORDER, ...Object.keys(checks ?? {}), ...Object.keys(disabled ?? {})])]
    : DEP_ORDER;

  return (
    <section className="orb-card mb-6 overflow-hidden">
      <div className="px-6 py-4 border-b border-border flex items-center gap-2">
        <Activity className="h-4 w-4 text-accent" aria-hidden="true" />
        <h2 className="orb-section-title text-sm">Service health</h2>
        {!checks && !disabled && (
          <span className="ml-auto text-xs text-fg-muted">
            Health checker not configured
          </span>
        )}
      </div>
      <ul>
        {keys.map((key, i) => {
          const status = checks?.[key];
          const disabledReason = disabled?.[key];
          const ok = status === 'ok';
          const unknown = status === undefined && disabledReason === undefined;
          const meta = DEP_META[key];
          const Icon = meta?.Icon ?? Server;
          const label = meta?.label ?? key;

          let statusBadge: JSX.Element;
          if (disabledReason !== undefined) {
            statusBadge = (
              <span
                className="inline-flex items-center gap-1 text-xs font-semibold text-fg-muted"
                title={disabledReason}
              >
                <MinusCircle className="h-3.5 w-3.5" aria-hidden="true" />
                Disabled
              </span>
            );
          } else if (unknown) {
            statusBadge = (
              <span className="inline-flex items-center gap-1 text-xs text-fg-subtle">
                <span className="h-2 w-2 rounded-full bg-fg-subtle inline-block" aria-hidden="true" />
                {'Unknown'}
              </span>
            );
          } else if (ok) {
            statusBadge = (
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-success">
                <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
                Healthy
              </span>
            );
          } else {
            statusBadge = (
              <span
                className="inline-flex items-center gap-1 text-xs font-semibold text-danger"
                title={status}
              >
                <XCircle className="h-3.5 w-3.5" aria-hidden="true" />
                <span className="max-w-[220px] truncate">{status}</span>
              </span>
            );
          }

          return (
            <li
              key={key}
              className={`
                flex items-center gap-4 px-6 py-3.5
                ${i < keys.length - 1 ? 'border-b border-border' : ''}
                ${i % 2 === 0 ? '' : 'bg-bg-inset/40'}
              `}
            >
              <Icon className="h-4 w-4 text-fg-muted shrink-0" aria-hidden="true" />
              <span className="flex-1 text-sm font-medium text-fg">{label}</span>
              {statusBadge}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// ---------------------------------------------------------------------------
// QueueDepthCard
// ---------------------------------------------------------------------------

function QueueDepthCard({ depth }: { readonly depth?: number }) {
  let iconColorClass: string;
  if (depth === undefined) {
    iconColorClass = 'bg-bg-inset text-fg-muted';
  } else if (depth === 0) {
    iconColorClass = 'bg-success/15 text-success';
  } else if (depth < 50) {
    iconColorClass = 'bg-accent/15 text-accent';
  } else {
    iconColorClass = 'bg-warning/15 text-warning';
  }

  let queueLabel: string;
  if (depth === undefined) {
    queueLabel = 'NATS not configured';
  } else if (depth === 0) {
    queueLabel = 'Queue empty — no pending scan jobs';
  } else {
    const plural = depth === 1 ? '' : 's';
    queueLabel = `pending scan job${plural}`;
  }

  return (
    <section className="orb-card mb-6 p-6 flex items-center gap-5">
      <div
        className={`
          h-12 w-12 rounded-full flex items-center justify-center shrink-0
          ${iconColorClass}
        `}
        aria-hidden="true"
      >
        <Activity className="h-5 w-5" />
      </div>
      <div>
        <p className="orb-eyebrow text-[11px] mb-0.5">Scan queue depth</p>
        {depth === undefined ? (
          <p className="text-2xl font-display font-semibold text-fg-muted">—</p>
        ) : (
          <p className="text-2xl font-display font-semibold text-fg">
            {depth.toLocaleString()}
          </p>
        )}
        <p className="text-xs text-fg-muted mt-0.5">
          {queueLabel}
        </p>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// AdvisorySyncCard (T644) — advisory-database freshness
// ---------------------------------------------------------------------------

function formatAge(seconds: number): string {
  const days = Math.floor(seconds / 86400);
  if (days >= 1) return `${days} day${days === 1 ? '' : 's'} ago`;
  const hours = Math.floor(seconds / 3600);
  if (hours >= 1) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const minutes = Math.max(1, Math.floor(seconds / 60));
  return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
}

function AdvisorySyncCard({ status }: { readonly status?: AdvisorySyncStatus }) {
  const stale =
    status?.age_seconds !== undefined &&
    status.age_seconds > ADVISORY_SYNC_STALE_THRESHOLD_SECONDS;
  const attemptFailed = status !== undefined && !status.success;

  let iconColorClass: string;
  if (!status || (status.age_seconds === undefined && !attemptFailed)) {
    iconColorClass = 'bg-bg-inset text-fg-muted';
  } else if (stale || attemptFailed) {
    iconColorClass = 'bg-warning/15 text-warning';
  } else {
    iconColorClass = 'bg-success/15 text-success';
  }

  let valueLabel: string;
  let captionLabel: string;
  if (status && status.age_seconds !== undefined) {
    valueLabel = formatAge(status.age_seconds);
    captionLabel = attemptFailed
      ? `last sync attempt failed (${status.mode}) — showing prior success`
      : `${status.mode} sync · advisory database up to date`;
  } else if (status) {
    valueLabel = 'never succeeded';
    captionLabel = `last attempt failed (${status.mode})`;
  } else {
    valueLabel = '—';
    captionLabel = 'advisory-sync has never run against this database';
  }

  return (
    <section className="orb-card mb-6 p-6 flex items-center gap-5">
      <div
        className={`
          h-12 w-12 rounded-full flex items-center justify-center shrink-0
          ${iconColorClass}
        `}
        aria-hidden="true"
      >
        <ShieldAlert className="h-5 w-5" />
      </div>
      <div>
        <p className="orb-eyebrow text-[11px] mb-0.5">Advisory database</p>
        <p className="text-2xl font-display font-semibold text-fg">
          {valueLabel}
        </p>
        <p className="text-xs text-fg-muted mt-0.5">
          {captionLabel}
        </p>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// SparklineCard — SVG bar chart, no chart library
// ---------------------------------------------------------------------------

function SparklineCard({ buckets }: { readonly buckets?: number[] }) {
  if (!buckets || buckets.length === 0) {
    return (
      <section className="orb-card p-6">
        <div className="flex items-center gap-2 mb-4">
          <Activity className="h-4 w-4 text-accent" aria-hidden="true" />
          <h2 className="orb-section-title text-sm">Scan rate — last 60 min</h2>
        </div>
        <p className="text-sm text-fg-muted">No scan data available.</p>
      </section>
    );
  }

  const max = Math.max(...buckets, 1);
  const BAR_W = 20;
  const BAR_GAP = 4;
  const CHART_H = 60;
  const total_w = buckets.length * (BAR_W + BAR_GAP) - BAR_GAP;
  const totalScans = buckets.reduce((s, v) => s + v, 0);

  return (
    <section className="orb-card p-6">
      <div className="flex items-center gap-2 mb-1">
        <Activity className="h-4 w-4 text-accent" aria-hidden="true" />
        <h2 className="orb-section-title text-sm">Scan rate — last 60 min</h2>
        <span className="ml-auto text-xs text-fg-muted">
          {totalScans.toLocaleString()} scan{totalScans === 1 ? '' : 's'} total
        </span>
      </div>
      <p className="text-xs text-fg-muted mb-4">5-minute buckets · oldest → newest</p>

      <svg
        viewBox={`0 0 ${total_w} ${CHART_H + 16}`}
        aria-label="Scan rate sparkline"
        role="img"
        className="w-full overflow-visible"
        style={{ height: CHART_H + 16 }}
      >
        {buckets.map((count, i) => {
          const barH = Math.max(2, Math.round((count / max) * CHART_H));
          const x = i * (BAR_W + BAR_GAP);
          const y = CHART_H - barH;
          const isNewest = i === buckets.length - 1;
          return (
            <g key={x} aria-label={`${count} scans`}>
              <rect
                x={x}
                y={y}
                width={BAR_W}
                height={barH}
                rx={2}
                className={isNewest ? 'fill-accent' : 'fill-accent/50'}
              />
              {count > 0 && (
                <text
                  x={x + BAR_W / 2}
                  y={CHART_H + 12}
                  textAnchor="middle"
                  fontSize="8"
                  className="fill-fg-muted"
                >
                  {count}
                </text>
              )}
            </g>
          );
        })}
        {/* x-axis line */}
        <line
          x1={0}
          y1={CHART_H}
          x2={total_w}
          y2={CHART_H}
          stroke="currentColor"
          strokeWidth={0.5}
          className="text-border"
        />
      </svg>

      <div className="flex justify-between text-[10px] text-fg-subtle mt-1">
        <span>−60 min</span>
        <span>now</span>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

function LoadingState() {
  return (
    <div className="orb-card p-8 flex items-center justify-center gap-3 text-fg-muted">
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
      <span className="text-sm">Loading system status…</span>
    </div>
  );
}

function ErrorBanner({ error }: { readonly error: unknown }) {
  let msg: string;
  if (error instanceof ApiError) {
    msg = `${error.code}: ${error.message}`;
  } else if (error instanceof Error) {
    msg = error.message;
  } else {
    msg = 'Unknown error';
  }
  const hint =
    error instanceof ApiError && error.code === 'insufficient_role'
      ? 'Only org admins can view the system dashboard.'
      : null;
  return (
    <div role="alert" className="orb-card p-6 border-danger/40 bg-danger/5">
      <p className="orb-eyebrow text-danger mb-2">request failed</p>
      <p className="text-sm text-fg">{msg}</p>
      {hint ? <p className="text-xs text-fg-muted mt-2">{hint}</p> : null}
    </div>
  );
}
